import { isDate } from 'lodash-es'
import { Trans, useTranslation } from 'react-i18next'
import { connect, DataItem } from 'scrivito'

import {
  ActivityChangeKind,
  getActivityChangeKind,
} from './getActivityChangeKind'
import {
  TimelineItemHeader,
  TimelineItemHeaderActor,
} from '@/Atomic/Molecules/TimelineItemHeader'
import { DataClassName } from '@/Data/const'
import { Contact } from '@/Data/Contact/ContactDataClass'
import { getAttributeDisplayValue } from '@/utils/CRUD/read/displayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { getDataItemInitials, getSchemaName } from '@/utils/dataClassMap'
import { isString } from '@/utils/isString'

interface ActivityTimelineHeaderProps {
  activity: DataItem
  className?: string
}

/**
 * Timeline header for a single activity: which kind of activity was created or
 * updated, by whom, and how long ago that happened.
 *
 * Maps the activity onto the presentational `TimelineItemHeader`: the wording
 * comes from the localized schema label of `subType` (falling back to `type`),
 * and the actor is the user of the described change (`createdBy` for a creation,
 * `updatedBy` for an update).
 *
 * The activity leads the sentence and the actor follows it ("Demo created by
 * Camille Moreau"), so no wording has to agree with the grammatical gender of
 * the person. Since the actor can be unknown, every change kind also has a
 * standalone variant without the trailing "by" part.
 */
export const ActivityTimelineHeader: React.FC<ActivityTimelineHeaderProps> =
  connect(({ activity, className }) => {
    const { t } = useTranslation()
    const { schemaAttributes } = getSchemaAttributes(
      getSchemaName(DataClassName.ACTIVITY),
    )

    const typeLabel =
      (getAttributeDisplayValue(
        schemaAttributes?.['subType'],
        activity.get('subType'),
      ) as string) ||
      (getAttributeDisplayValue(
        schemaAttributes?.['type'],
        activity.get('type'),
      ) as string) ||
      t('components.activity.timeline.defaultType')

    const changeKind = getActivityChangeKind(
      activity.get('createdAt'),
      activity.get('updatedAt'),
    )

    const actor = getActor(activity, changeKind)
    const actorName = actor?.get('name') as string | undefined

    return (
      <TimelineItemHeader
        className={className}
        action={
          actorName ? (
            <Trans
              i18nKey={`components.activity.timeline.${changeKind}WithActor`}
              values={{ type: typeLabel, actor: actorName }}
              components={{ actor: <TimelineItemHeaderActor /> }}
            />
          ) : (
            t(`components.activity.timeline.${changeKind}`, { type: typeLabel })
          )
        }
        changedAt={getChangedAt(activity)}
        actorAvatar={actor?.get('imageThumbnail') as DataBinary | undefined}
        actorInitials={getDataItemInitials(actor)}
        actorId={actor?.id()}
      />
    )
  })

/**
 * The design shows the last change, so `updatedAt` wins over `createdAt`. For a
 * never updated activity both timestamps are identical anyway.
 */
const getChangedAt = (activity: DataItem) => {
  const changedAt = activity.get('updatedAt') ?? activity.get('createdAt')
  if (isString(changedAt) || isDate(changedAt)) return changedAt
  return undefined
}

/**
 * The actor belongs to the change the header describes, so it follows the change
 * kind instead of falling back between both fields. Attributing an update to the
 * creator would name the wrong person, so an unknown actor stays unknown.
 */
const getActor = (activity: DataItem, changeKind: ActivityChangeKind) => {
  const actorId = activity.get(
    changeKind === 'created' ? 'createdBy' : 'updatedBy',
  )
  return isString(actorId) ? Contact.get(actorId) : null
}
